import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trophy, HelpCircle, Undo } from 'lucide-react';

/**
 * Zip Game (Numbrix / Number Snake)
 * 
 * Goal: Fill the grid with a continuous path of numbers from 1 to N*N.
 * Rules:
 * - Numbers must be sequential (1, 2, 3...)
 * - Sequential numbers must be horizontally or vertically adjacent.
 * - Fill every cell.
 */
const ZipGame = () => {
    const navigate = useNavigate();

    // --- Configuration ---
    const DIFFICULTY_SETTINGS = {
        Easy: { size: 5, hiddenRatio: 0.5 },
        Medium: { size: 7, hiddenRatio: 0.6 },
        Hard: { size: 9, hiddenRatio: 0.7 },
    };

    const [difficulty, setDifficulty] = useState('Easy');
    const [gameStatus, setGameStatus] = useState('loading'); // loading, playing, won
    const [gridSize, setGridSize] = useState(5);
    
    // Grid state: 
    // val: number (1..max)
    // type: 'fixed' (initial clue) | 'user' (filled by player) | null (empty)
    const [grid, setGrid] = useState([]); 
    
    // Track the "next number" to place for convenience
    const [nextNumber, setNextNumber] = useState(2); 

    const [showRules, setShowRules] = useState(false);
    const [history, setHistory] = useState([]); // For Undo functionality

    // --- Generator Logic (Warnsdorff's optimized) ---
    const generatePath = useCallback((size) => {
        const totalCells = size * size;
        const isValid = (r, c) => r >= 0 && r < size && c >= 0 && c < size;
        const getNeighbors = (r, c, currentGrid) => [
            [r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]
        ].filter(([nr, nc]) => isValid(nr, nc) && currentGrid[nr][nc] === 0);

        const getSortedNeighbors = (r, c, currentGrid) => {
            const neighbors = getNeighbors(r, c, currentGrid);
            const neighborsWithDegree = neighbors.map(([nr, nc]) => {
                const degree = getNeighbors(nr, nc, currentGrid).length;
                return { r: nr, c: nc, degree };
            });
            neighborsWithDegree.sort((a, b) => {
                if (a.degree === b.degree) return Math.random() - 0.5;
                return a.degree - b.degree;
            });
            return neighborsWithDegree.map(n => [n.r, n.c]);
        };

        // Safety cap mechanism
        let steps = 0;
        const MAX_STEPS = 15000;

        const solve = (r, c, count, currentGrid) => {
            steps++;
            if (steps > MAX_STEPS) return false;

            currentGrid[r][c] = count;
            if (count === totalCells) return true;
            
            const neighbors = getSortedNeighbors(r, c, currentGrid);
            for (const [nr, nc] of neighbors) {
                if (solve(nr, nc, count + 1, currentGrid)) return true;
                if (steps > MAX_STEPS) return false; // Propagate abort
            }
            currentGrid[r][c] = 0;
            return false;
        };

        for (let attempt = 0; attempt < 3; attempt++) {
            steps = 0; // Reset steps for new attempt
            const pathGrid = Array(size).fill(null).map(() => Array(size).fill(0));
            const startR = Math.floor(Math.random() * size);
            const startC = Math.floor(Math.random() * size);
            if (solve(startR, startC, 1, pathGrid)) return pathGrid;
        }
        
        // Fallback snake
        const fallbackGrid = Array(size).fill(null).map(() => Array(size).fill(0));
        let count = 1;
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                const col = r % 2 === 0 ? c : size - 1 - c;
                fallbackGrid[r][col] = count++;
            }
        }
        return fallbackGrid;
    }, []);

    const newGame = useCallback(() => {
        setGameStatus('loading');
        setTimeout(() => {
            const settings = DIFFICULTY_SETTINGS[difficulty];
            const size = settings.size;
            setGridSize(size);

            const fullPath = generatePath(size);
            const newGrid = fullPath.map((row, r) => 
                row.map((val, c) => {
                    const isStart = val === 1;
                    const isEnd = val === size * size;
                    const shouldKeep = Math.random() > settings.hiddenRatio;
                    
                    if (isStart || isEnd || shouldKeep) {
                        return { val, type: 'fixed' };
                    }
                    return { val: null, type: null };
                })
            );

            setGrid(newGrid);
            setHistory([]);
            setGameStatus('playing');
        }, 10);
    }, [difficulty, generatePath]);

    useEffect(() => {
        newGame();
    }, [newGame]);

    // Recalculate 'nextNumber' whenever grid changes
    useEffect(() => {
        if (gameStatus !== 'playing') return;

        const flatValues = new Set();
        grid.forEach(row => row.forEach(cell => {
            if (cell.val) flatValues.add(cell.val);
        }));

        let k = 1;
        while (flatValues.has(k)) {
            k++;
        }
        setNextNumber(k);

        // Check Win
        const total = gridSize * gridSize;
        if (flatValues.size === total) {
            let valid = true;
            for (let r = 0; r < gridSize; r++) {
                for (let c = 0; c < gridSize; c++) {
                    const val = grid[r][c].val;
                    if (val < total) {
                        const neighbors = [
                            [r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]
                        ];
                        const hasSequentialNeighbor = neighbors.some(([nr, nc]) => 
                            nr >= 0 && nr < gridSize && nc >= 0 && nc < gridSize && 
                            grid[nr][nc].val === val + 1
                        );
                        if (!hasSequentialNeighbor) valid = false;
                    }
                }
            }
            if (valid) setGameStatus('won');
        }

    }, [grid, gridSize, gameStatus]);


    // --- Interaction ---

    const handleCellClick = (r, c) => {
        if (gameStatus !== 'playing') return;
        
        const cell = grid[r][c];
        if (cell.type === 'fixed') return;

        if (cell.type === 'user') {
            const newGrid = [...grid];
            newGrid[r] = [...newGrid[r]];
            newGrid[r][c] = { val: null, type: null };
            setGrid(newGrid);
            return;
        }

        const newGrid = [...grid];
        newGrid[r] = [...newGrid[r]];
        newGrid[r][c] = { val: nextNumber, type: 'user' };
        setGrid(newGrid);
        
        setHistory(prev => [...prev, { r, c }]);
    };
    
    const handleUndo = () => {
        if (history.length === 0) return;
        const lastMove = history[history.length - 1];
        const { r, c } = lastMove;
        
        if (grid[r][c].type === 'user') {
            const newGrid = [...grid];
            newGrid[r] = [...newGrid[r]];
            newGrid[r][c] = { val: null, type: null };
            setGrid(newGrid);
        }
        
        setHistory(prev => prev.slice(0, -1));
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            {/* Header */}
            <div className="w-full max-w-2xl px-4 mb-8 flex items-center justify-between">
                <button
                    onClick={() => navigate("/games")}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                    <ArrowLeft className="h-5 w-5 mr-1" /> 
                    Back to Games
                </button>
                <div className="text-xl font-bold text-gray-800">Zip Game</div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowRules(!showRules)}
                        className={`p-2 rounded transition-colors ${showRules ? "text-blue-600 bg-blue-50" : "text-gray-500 hover:text-blue-600"}`}
                        title="How to Play"
                    >
                        <HelpCircle className="h-5 w-5" />
                    </button>
                    <button
                        onClick={newGame}
                        className="p-2 text-gray-500 hover:text-blue-600"
                        title="New Game"
                    >
                         <RefreshCw className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Rules */}
            {showRules && (
                <div className="max-w-xl w-full bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-blue-900 animate-fade-in shadow-sm">
                    <h3 className="font-bold text-lg mb-3">How to Play Zip</h3>
                    <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                        <li><strong>Goal:</strong> Fill the grid so that numbers 1 to {gridSize*gridSize} follow a continuous path.</li>
                        <li><strong>Movement:</strong> The path can move <strong>Up, Down, Left, or Right</strong> (no diagonals).</li>
                        <li><strong>Next Number:</strong> The highlighted number ({nextNumber}) is the next one to place. Click any empty spot to place it!</li>
                        <li><strong>Tip:</strong> Look for the "Fixed" numbers (in bold/darker color) to guide your path.</li>
                    </ul>
                    <button 
                        onClick={() => setShowRules(false)}
                        className="mt-4 text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wide"
                    >
                        Close Rules
                    </button>
                </div>
            )}

            {/* Controls */}
            <div className="flex gap-4 mb-6">
                <div className="bg-white rounded-lg p-1 flex shadow-sm border border-gray-200">
                    {Object.keys(DIFFICULTY_SETTINGS).map((diff) => (
                        <button 
                            key={diff}
                            onClick={() => setDifficulty(diff)}
                            className={`px-4 py-1 rounded text-sm font-medium transition-colors ${difficulty === diff ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}
                        >
                            {diff} ({DIFFICULTY_SETTINGS[diff].size}x{DIFFICULTY_SETTINGS[diff].size})
                        </button>
                    ))}
                </div>
                <button
                    onClick={handleUndo}
                    disabled={history.length === 0}
                    className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                    <Undo className="w-4 h-4 mr-2" /> Undo
                </button>
            </div>

            {/* Status Indicator */}
            <div className="mb-4 text-lg font-medium text-gray-700">
                Next Number: <span className="text-blue-600 font-bold text-2xl">{nextNumber <= gridSize*gridSize ? nextNumber : "Done!"}</span>
            </div>

            {/* Game Board */}
            <div className="relative bg-white p-4 rounded-xl shadow-xl border border-gray-200">
                 {gameStatus === "won" && (
                    <div className="absolute inset-0 bg-white/90 z-10 flex flex-col items-center justify-center animate-fade-in backdrop-blur-sm rounded-xl">
                        <Trophy className="w-20 h-20 text-yellow-500 mb-4" />
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Zip Completed!</h2>
                        <button 
                             onClick={newGame}
                             className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg font-medium"
                        >
                            Play Again
                        </button>
                    </div>
                 )}

                 <div 
                    className="grid gap-1 bg-gray-200 border-2 border-gray-300"
                    style={{ 
                        gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                        padding: '4px'
                    }}
                 >
                    {grid.map((row, r) => (
                        row.map((cell, c) => {
                            const isFixed = cell.type === 'fixed';
                            const isUser = cell.type === 'user';
                            const val = cell.val;
                            
                            return (
                                <button
                                    key={`${r}-${c}`}
                                    onClick={() => handleCellClick(r, c)}
                                    className={`
                                        w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 
                                        flex items-center justify-center text-lg sm:text-xl font-bold rounded
                                        transition-all duration-200
                                        ${isFixed ? "bg-gray-800 text-white cursor-default" : 
                                          isUser ? "bg-blue-500 text-white shadow-md transform scale-105" : 
                                          "bg-white hover:bg-gray-100 text-blue-400"}
                                    `}
                                >
                                    {val}
                                </button>
                            );
                        })
                    ))}
                 </div>
            </div>
        </div>
    );
};

export default ZipGame;
